import React, { useState, useEffect } from 'react';
import {
  Shield, AlertTriangle, AlertOctagon, CheckCircle, XCircle,
  Clock, ChevronDown, ChevronUp, Lock, Key, Users, Server,
  ArrowRight, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getDeploymentGates, approveDeployment, rejectDeployment } from '../services/api';
import { toast } from 'sonner';

const severityConfig = {
  critical: { color: '#FF3B30', bg: 'bg-[#FF3B30]/10', border: 'border-[#FF3B30]/30', label: 'CRITICAL' },
  high: { color: '#FF6961', bg: 'bg-[#FF6961]/10', border: 'border-[#FF6961]/30', label: 'HIGH' },
  medium: { color: '#FFCC00', bg: 'bg-[#FFCC00]/10', border: 'border-[#FFCC00]/30', label: 'MEDIUM' },
  low: { color: '#34C759', bg: 'bg-[#34C759]/10', border: 'border-[#34C759]/30', label: 'LOW' },
};

const statusConfig = {
  pending: { color: '#FFCC00', icon: Clock, label: 'Pending Review' },
  approved: { color: '#34C759', icon: CheckCircle, label: 'Approved' },
  rejected: { color: '#FF3B30', icon: XCircle, label: 'Rejected' },
};

const DeploymentGatePage = () => {
  const [gates, setGates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGate, setExpandedGate] = useState(null);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchGates();
  }, []);

  const fetchGates = async () => {
    try {
      const resp = await getDeploymentGates();
      setGates(resp.data);
    } catch (err) {
      console.error('Failed to load deployment gates', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (gateId) => {
    setActionLoading(gateId);
    try {
      await approveDeployment(gateId);
      toast.success('Deployment approved');
      fetchGates();
    } catch (err) {
      toast.error('Failed to approve deployment');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (gateId) => {
    setActionLoading(gateId);
    try {
      await rejectDeployment(gateId);
      toast.success('Deployment rejected');
      fetchGates();
    } catch (err) {
      toast.error('Failed to reject deployment');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredGates = filter === 'all' ? gates : gates.filter(g => g.status === filter);
  const pendingCount = gates.filter(g => g.status === 'pending').length;
  const criticalPending = gates.filter(g => g.status === 'pending' && g.risk_level === 'critical').length;

  const formatTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const now = new Date();
    const diffH = Math.round((now - d) / (1000 * 60 * 60));
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    return `${Math.round(diffH / 24)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#9CA3AF]">Loading deployment gates...</div>
      </div>
    );
  }

  return (
    <div data-testid="deployment-gate-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Lock className="w-6 h-6 text-[#0066FF]" />
            <h1 className="text-2xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Deployment Gate
            </h1>
          </div>
          <p className="text-sm text-[#6B7280]">
            Pre-production approval pipeline &middot; Nothing reaches production without review
          </p>
        </div>
      </div>

      {/* Alert Banner for Critical Pending */}
      {criticalPending > 0 && (
        <div className="p-4 border border-[#FF3B30]/30 bg-[#FF3B30]/10" data-testid="critical-pending-banner">
          <div className="flex items-center gap-3">
            <AlertOctagon className="w-5 h-5 text-[#FF3B30]" />
            <span className="text-[#F9FAFB] text-sm font-medium">
              {criticalPending} critical-risk {criticalPending === 1 ? 'agent requires' : 'agents require'} immediate review before production deployment
            </span>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#F9FAFB]">{gates.length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Total Requests</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#FFCC00]">{pendingCount}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Pending Review</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#34C759]">{gates.filter(g => g.status === 'approved').length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Approved</div>
        </div>
        <div className="enterprise-card p-4">
          <div className="text-2xl font-bold font-mono text-[#FF3B30]">{gates.filter(g => g.status === 'rejected').length}</div>
          <div className="text-[10px] text-[#6B7280] uppercase tracking-wider">Rejected</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'rejected'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${
              filter === f
                ? 'bg-[#0066FF] text-white'
                : 'bg-[#1A1A1A] text-[#9CA3AF] hover:text-[#F9FAFB]'
            }`}
            data-testid={`filter-${f}`}
          >
            {f === 'all' ? 'All' : f}
            {f === 'pending' && pendingCount > 0 && (
              <span className="ml-2 bg-[#FFCC00]/20 text-[#FFCC00] px-1.5 py-0.5 text-[10px]">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Gate List */}
      <div className="space-y-3">
        {filteredGates.length === 0 ? (
          <div className="enterprise-card p-12 text-center">
            <Shield className="w-10 h-10 mx-auto mb-3 text-[#34C759]" />
            <p className="text-[#9CA3AF]">No deployment requests match this filter</p>
          </div>
        ) : (
          filteredGates.map((gate) => {
            const riskCfg = severityConfig[gate.risk_level] || severityConfig.medium;
            const stCfg = statusConfig[gate.status] || statusConfig.pending;
            const isExpanded = expandedGate === gate.id;

            return (
              <div
                key={gate.id}
                className={`enterprise-card border-l-4 transition-all`}
                style={{ borderLeftColor: riskCfg.color }}
                data-testid={`gate-${gate.id}`}
              >
                {/* Main Row */}
                <div
                  className="p-5 cursor-pointer"
                  onClick={() => setExpandedGate(isExpanded ? null : gate.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[#F9FAFB] font-mono text-sm font-semibold">{gate.agent_name}</span>
                          <span className="text-[10px] font-mono uppercase px-2 py-0.5" style={{ color: riskCfg.color, backgroundColor: `${riskCfg.color}15` }}>
                            {riskCfg.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-[#6B7280]">
                          <span>{gate.environment_from} <ArrowRight className="w-3 h-3 inline" /> {gate.environment_to}</span>
                          <span>&middot;</span>
                          <span>Requested by {gate.requested_by}</span>
                          <span>&middot;</span>
                          <span>{formatTime(gate.requested_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Risk Score */}
                      <div className="text-center">
                        <div className="text-xl font-mono font-bold" style={{ color: riskCfg.color }}>{gate.risk_score}</div>
                        <div className="text-[10px] text-[#6B7280] uppercase">Risk</div>
                      </div>
                      {/* Blockers count */}
                      <div className="text-center">
                        <div className={`text-xl font-mono font-bold ${gate.blockers?.length > 0 ? 'text-[#FF3B30]' : 'text-[#34C759]'}`}>
                          {gate.blockers?.length || 0}
                        </div>
                        <div className="text-[10px] text-[#6B7280] uppercase">Blockers</div>
                      </div>
                      {/* Status */}
                      <div className="flex items-center gap-1.5 px-3 py-1.5" style={{ backgroundColor: `${stCfg.color}15` }}>
                        <stCfg.icon className="w-3.5 h-3.5" style={{ color: stCfg.color }} />
                        <span className="text-xs font-mono" style={{ color: stCfg.color }}>{stCfg.label}</span>
                      </div>
                      {/* Chevron */}
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-[#6B7280]" /> : <ChevronDown className="w-4 h-4 text-[#6B7280]" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-[#27272A]/40">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-4">
                      {/* Blockers */}
                      <div>
                        <h3 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">
                          Blockers & Policy Violations
                        </h3>
                        {gate.blockers && gate.blockers.length > 0 ? (
                          <div className="space-y-2">
                            {gate.blockers.map((b, i) => (
                              <div key={i} className="p-3 bg-[#FF3B30]/5 border border-[#FF3B30]/20">
                                <div className="flex items-start gap-2">
                                  <AlertTriangle className="w-4 h-4 text-[#FF3B30] mt-0.5 shrink-0" />
                                  <div>
                                    <div className="text-xs font-mono text-[#FF6961] uppercase">{b.type?.replace(/_/g, ' ')}</div>
                                    <div className="text-xs text-[#9CA3AF] mt-0.5">{b.detail}</div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-3 bg-[#34C759]/5 border border-[#34C759]/20">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-[#34C759]" />
                              <span className="text-xs text-[#34C759]">No blockers — all checks passed</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Permissions & Tools */}
                      <div>
                        <h3 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-3">
                          Requested Access
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <div className="text-[10px] text-[#6B7280] uppercase mb-1">Tools ({gate.tools_requested?.length || 0})</div>
                            <div className="flex flex-wrap gap-1">
                              {(gate.tools_requested || []).map((t, i) => (
                                <span key={i} className="text-[10px] font-mono px-2 py-1 bg-[#1A1A1A] text-[#9CA3AF] border border-[#27272A]/40">
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="text-[10px] text-[#6B7280] uppercase mb-1">Permissions ({gate.permissions_requested?.length || 0})</div>
                            <div className="flex flex-wrap gap-1">
                              {(gate.permissions_requested || []).map((p, i) => (
                                <span key={i} className="text-[10px] font-mono px-2 py-1 bg-[#0066FF]/10 text-[#0066FF] border border-[#0066FF]/20">
                                  {p}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Review Info */}
                        {gate.reviewed_by && (
                          <div className="mt-4 p-3 bg-[#1A1A1A] border border-[#27272A]/40">
                            <div className="text-[10px] text-[#6B7280] uppercase mb-1">Reviewed By</div>
                            <div className="text-sm text-[#F9FAFB]">{gate.reviewed_by}</div>
                            <div className="text-[10px] text-[#6B7280]">{gate.reviewed_at ? new Date(gate.reviewed_at).toLocaleString() : '—'}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons — only for pending */}
                    {gate.status === 'pending' && (
                      <div className="flex gap-3 mt-5 pt-4 border-t border-[#27272A]/40">
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleReject(gate.id); }}
                          disabled={actionLoading === gate.id}
                          className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20 px-6"
                          data-testid={`reject-${gate.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-2" /> Reject Deployment
                        </Button>
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleApprove(gate.id); }}
                          disabled={actionLoading === gate.id}
                          className="bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30 hover:bg-[#34C759]/20 px-6"
                          data-testid={`approve-${gate.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" /> Approve for Production
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DeploymentGatePage;
